/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Nov 08 2024
 **/

import { has } from "lodash-es";
import produce from "immer";

import { send_message } from "@/frameworks/core/unity";
import { Configure } from "@/frameworks/libraries/utilities";

import { MyCurrentSpace } from "./states";
import usePersistStore from "../usePersistStore";
import { checkPriorityCurrentSpaceType } from "../../utils";

const LOGTAG = "#Unity:Actions#";

/**
 * UnityActions provides methods to handle incoming messages and update Unity user information.
 */
export default class UnityActions {
	private static _instance: UnityActions | null = null;
	private constructor() {}
	public static get instance() {
		if (!UnityActions._instance) {
			UnityActions._instance = new UnityActions();
		}
		return UnityActions._instance;
	}

	/**
	 * Handles an incoming message received via a STOMP protocol.
	 *
	 * @param {Object} message - The message frame containing the type and payload.
	 * @param {string} message.type - The type of the message (e.g., "userUpdated", "userBlock").
	 * @param {Object} message.payload - The payload of the message, varies based on message type.
	 * @param {string} topic - The topic from which the message was received.
	 * @return {Promise<void>} A promise resolving to void once message processing is complete.
	 */
	public async on_message_received(
		{ type, payload }: Message.Frame<Message.StompMessageTypes, any, Message.Options>,
		topic: string
	): Promise<void> {
		switch (type) {
			case "userUpdated": {
				this.update_unity_userinfo(payload);
				break;
			}
		}
	}

	/**
	 * Updates the Unity user information if certain conditions are met.
	 *
	 * @param {PushServices.User.UserUpdated.Data} payload - The updated user profile information.
	 * @return {void}
	 * @private
	 */
	private update_unity_userinfo(payload: PushServices.User.UserUpdated.Data): void {
		if (Configure.userID === payload.userInfo.id) {
			if (has(payload.updatedInfo, "name") || has(payload.updatedInfo, "profileImagePath")) {
				const unity_payload: IPC.Actions.UpdateUserProfile = {
					profileImagePath: payload.updatedInfo.profileImagePath || payload.userInfo.profileIamgePath,
					name: payload.updatedInfo.name || payload.userInfo.name,
					userID: payload.userInfo.id
				};
				send_message({
					type: "updateUserProfile",
					payload: unity_payload
				});
			}
		}
	}

	/**
	 * Retrieves the current space of the user.
	 */
	public get_my_current_space(): MyCurrentSpace["myCurrentSpace"]["currentSpace" | "currentConference"] | undefined {
		const { users } = usePersistStore.getState();
		const user = users[Configure.userID];
		if (user) {
			const { currentSpace, currentConference } = user;
			const spaceType = checkPriorityCurrentSpaceType({ currentSpace, currentConference });

			switch (spaceType) {
				case "SPACE":
					return currentSpace;
				case "CONFERENCE":
					return currentConference;
				default:
					return undefined;
			}
		}
	}

	/**
	 * Updates the current space of the user(only me).
	 */
	public set_my_current_space(
		payload: {
			currentSpace?: MyCurrentSpace["myCurrentSpace"]["currentSpace"];
			currentConference?: MyCurrentSpace["myCurrentSpace"]["currentConference"];
		} | null
	): void {
		const { unity } = usePersistStore.getState();

		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				draft.unity = {
					...unity,
					myCurrentSpace: payload
						? {
								...unity.myCurrentSpace,
								currentSpace: payload.currentSpace,
								currentConference: payload.currentConference
							}
						: undefined
				};
			})
		);
	}
}
